import isEqual from 'lodash.isequal';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { RouteMeta } from 'vue-router';

import { useEnvBoolean } from '@/composables/useEnvBoolean.composable';
import {
  type EnumOrganizationMemberPermissions,
  EnumOrganizationPermissions,
  EnumPermissionContextIds,
} from '@/helpers/enums/permissions.enum';
import { EnumRoutes } from '@/helpers/enums/routes.enum';
import type { Permission } from '@/helpers/interfaces/permissoins.interface';
import { axiosI } from '@/plugins';
import router from '@/router';
import { useOrganizationStore } from '@/stores/organzation.pinia';
import { authApiHandler } from '@/submodules/postman-to-ts/apiHandler/auth';
import { miscApiHandler } from '@/submodules/postman-to-ts/apiHandler/misc';

import { useAuthStore } from './auth.store';

export const usePermissionsStore = defineStore('PermissionsStore', () => {
  const systemPermissions = ref<Permission[]>([] as Permission[]);
  const userPermissions = ref<Permission[]>([] as Permission[]);

  function checkPermission(
    permissions: Array<
      EnumOrganizationMemberPermissions | EnumOrganizationPermissions
    >
  ): boolean {
    if (permissions.length === 0) return true;

    const userHasSlug = (slug: string) =>
      userPermissions.value?.some((up) => up.slug === slug) ?? false;

    const checkOne = (slug: string): boolean => {
      const def = systemPermissions.value?.find((p) => p?.slug === slug);

      // Bypass grants the member-level ownership, but the target
      // permission's own prerequisites (org-level feature flags) must
      // still hold.
      const bypassSlug = getBypassPermissionName(def?.contextId);
      if (bypassSlug && userHasSlug(bypassSlug)) {
        const targetPrereqsOk =
          !def?.prerequisites?.length ||
          def.prerequisites.every((pr) => checkOne(pr.prerequisite.slug));
        if (targetPrereqsOk) return true;
      }

      const prereqsOk =
        !def?.prerequisites?.length ||
        def.prerequisites.every((pr) => checkOne(pr.prerequisite.slug));
      return prereqsOk && userHasSlug(slug);
    };

    return permissions.every(checkOne);
  }

  // OR variant — used by route metas that declare `*PermissionsAny` to
  // mean "user must hold at least one of these". Prerequisite handling is
  // unchanged: each candidate slug still needs its own prerequisite chain.
  function checkPermissionAny(
    permissions: Array<
      EnumOrganizationMemberPermissions | EnumOrganizationPermissions
    >
  ): boolean {
    if (permissions.length === 0) return true;
    return permissions.some((slug) => checkPermission([slug]));
  }
  function getBypassPermissionName(
    permissionContextId: EnumPermissionContextIds | undefined
  ) {
    switch (permissionContextId) {
      case EnumPermissionContextIds.OrganizationMember:
        return 'organization_member_full_access';
      case EnumPermissionContextIds.User:
        return 'user_everything';
      case EnumPermissionContextIds.CourseInstructor:
        return 'whatever';
      case EnumPermissionContextIds.Global:
        return 'global_everything';
      case EnumPermissionContextIds.Organization:
        return 'organization_everything';
      default:
        return 'never_gonna_give_you_up';
    }
  }

  //to be called from outside
  function setPermissions() {
    const systemPermissionsFromStorage =
      localStorage.getItem('systemPermissions');
    systemPermissions.value = systemPermissionsFromStorage
      ? JSON.parse(systemPermissionsFromStorage)
      : [];
    const userPermissionsFromStorage = localStorage.getItem('userPermissions');
    userPermissions.value = userPermissionsFromStorage
      ? JSON.parse(userPermissionsFromStorage)
      : [];
  }

  // to be called on auth change
  async function initializePermissions() {
    return await Promise.all([getAllPermissions(), getUserPermissions()]);
  }
  async function getAllPermissions() {
    const { path, method } =
      miscApiHandler.permissionsSystem.permissions.getAllPermissions;
    try {
      const { data, status } = await axiosI<{
        count: number;
        data: Permission[];
      }>(path(), {
        method,
        params: {
          take: Number.MAX_SAFE_INTEGER,
        },
      });
      systemPermissions.value = data.data;
      const systemPermissionsFromStorage =
        localStorage.getItem('systemPermissions');
      const convertedSystemPermissions: Permission[] = JSON.parse(
        systemPermissionsFromStorage ?? '[]'
      );
      if (
        systemPermissionsFromStorage &&
        !isEqual(convertedSystemPermissions, data.data)
      ) {
        localStorage.setItem('systemPermissions', JSON.stringify(data.data));
        // instead of reloading the page just check if he have the permission to access the current page
        // window.location.reload();
        const currentRoute = router.currentRoute.value;
        const havePermission = usePermissionsStore().checkAuthentication(
          currentRoute.meta
        );
        if (havePermission) return;
        else await router.replace({ name: EnumRoutes.landing.root.name });
      }
      localStorage.setItem('systemPermissions', JSON.stringify(data.data));
      return { data, status };
    } catch (error: any) {
      return { error };
    }
  }

  async function getUserPermissions() {
    const { path, method } = authApiHandler.getUserPermissions;
    try {
      const { data, status } = await axiosI<{
        count: number;
        data: Permission[];
      }>(path(), {
        method,
      });
      userPermissions.value = data.data;
      const userPermissionsFromStorage =
        localStorage.getItem('userPermissions');
      const convertedUserPermissions: Permission[] = JSON.parse(
        userPermissionsFromStorage ?? '[]'
      );
      if (
        userPermissionsFromStorage &&
        !isEqual(convertedUserPermissions, data.data)
      ) {
        localStorage.setItem('userPermissions', JSON.stringify(data.data));
        // instead of reloading the page just check if he have the permission to access the current page
        // window.location.reload();
        const currentRoute = router.currentRoute.value;
        const havePermission = usePermissionsStore().checkAuthentication(
          currentRoute.meta
        );
        if (havePermission) return;
        else await router.replace({ name: EnumRoutes.landing.root.name });
      }
      localStorage.setItem('userPermissions', JSON.stringify(data.data));
      return { data, status };
    } catch (error: any) {
      return { error };
    }
  }

  // can be used before each route or to show the components
  // requiresAuth: need to be logged in
  // memberPermissions: need to have user permission (when logged in)
  // organizationPermissions: need to have organization permission (when not logged)
  function checkAuthentication(
    meta: RouteMeta,
    beforeRoute: boolean = false,
    to: string = ''
  ) {
    if (meta.groupMeta) {
      const authArray = meta.groupMeta.map((item) => {
        return checkSingleMeta(item, beforeRoute, to);
      });
      const auth = authArray.some((item) => item.auth);
      if (beforeRoute) {
        if (authArray.every((item) => item.shouldHavePermission))
          useAuthStore().shouldHavePermission = true;
        else if (authArray.some((item) => item.shouldHavePermission === false))
          useAuthStore().shouldHavePermission = false;

        if (authArray.every((item) => item.shouldLogged))
          useAuthStore().shouldLogged = true;
        else if (authArray.some((item) => item.shouldLogged === false))
          useAuthStore().shouldLogged = false;

        const previousRoute = authArray.find((item) => item.previousRoute);
        if (previousRoute?.previousRoute)
          useAuthStore().setPreviousRoute(previousRoute.previousRoute);
      }

      return auth;
    } else {
      const { auth, shouldHavePermission, shouldLogged, previousRoute } =
        checkSingleMeta(meta, beforeRoute, to);
      if (beforeRoute) {
        if (shouldLogged !== undefined)
          useAuthStore().shouldLogged = shouldLogged;
        if (shouldHavePermission !== undefined)
          useAuthStore().shouldHavePermission = shouldHavePermission;
        if (previousRoute !== undefined)
          useAuthStore().setPreviousRoute(previousRoute);
      }
      return auth;
    }
  }
  function checkOrganizationConfig(meta: RouteMeta) {
    const orgStore = useOrganizationStore();
    if (
      meta.shouldAllowAnonymousCourseView &&
      !orgStore.allowAnonymousCourseView &&
      !useAuthStore().user?.id
    )
      return false;
    if (
      meta.shouldAllowAnonymousExternalCourseView &&
      !orgStore.allowAnonymousExternalCourseView &&
      !useAuthStore().user?.id
    )
      return false;
    if (
      meta.shouldAllowAnonymousEventView &&
      !orgStore.allowAnonymousEventView &&
      !useAuthStore().user?.id
    )
      return false;
    if (meta.shouldHaveCertificatesEnabled && !orgStore.certificatesEnabled)
      return false;
    return true;
  }
  function checkSingleMeta(
    meta: RouteMeta,
    beforeRoute: boolean = false,
    to: string = ''
  ): {
    auth: boolean;
    shouldHavePermission?: boolean;
    shouldLogged?: boolean;
    previousRoute?: string;
  } {
    if (meta.isHiddenRoute && !useEnvBoolean().allowLandingPage)
      return { auth: false };
    // beforeRoute and to are used for the route guard not for the component guard
    const requiresAuth = (meta?.requiresAuth as boolean) ?? false;
    const memberPermissions =
      (meta?.memberPermissions as EnumOrganizationMemberPermissions[]) ?? [];
    const organizationPermissions =
      (meta?.organizationPermissions as EnumOrganizationPermissions[]) ?? [];
    const memberPermissionsAny =
      (meta?.memberPermissionsAny as EnumOrganizationMemberPermissions[]) ?? [];
    const organizationPermissionsAny =
      (meta?.organizationPermissionsAny as EnumOrganizationPermissions[]) ?? [];

    // Unverified authed user: block everything except allowUnverified routes.
    const user = useAuthStore().user;
    if (user && user.isVerified === false && !meta.allowUnverified) {
      return { auth: false };
    }

    const isAuthed = !!user;

    // Org-level gates apply to everyone (authed and guest). Evaluate them
    // first — there's no point asking a guest to log in if the org itself
    // doesn't permit the feature.
    const orgOk =
      checkPermission(organizationPermissions) &&
      checkPermissionAny(organizationPermissionsAny);
    if (!orgOk || !checkOrganizationConfig(meta)) {
      return {
        auth: false,
        shouldHavePermission: beforeRoute
          ? true
          : useAuthStore().shouldHavePermission,
      };
    }

    // Login requirement: route asks for an authed user but caller is guest.
    if (!isAuthed && requiresAuth) {
      return {
        auth: false,
        shouldLogged: beforeRoute ? true : undefined,
        previousRoute: beforeRoute ? to : undefined,
      };
    }

    // Member-level gates apply only to authed users (memberPermissions are
    // member-of-organization perms; guests have no membership).
    const memberOk =
      !isAuthed ||
      (checkPermission(memberPermissions) &&
        checkPermissionAny(memberPermissionsAny));
    if (!memberOk) {
      return {
        auth: false,
        shouldHavePermission: beforeRoute
          ? true
          : useAuthStore().shouldHavePermission,
      };
    }

    return { auth: true };
  }

  return {
    systemPermissions,
    userPermissions,
    checkPermission,
    getBypassPermissionName,
    initializePermissions,
    getAllPermissions,
    getUserPermissions,
    setPermissions,
    checkAuthentication,
    checkOrganizationConfig,
  };
});
